import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CurrencyEur, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/fmt";
import StatusBadge from "@/components/StatusBadge";

const PERIODS = [
  { k: "day", l: "Aujourd'hui" },
  { k: "week", l: "Cette semaine" },
  { k: "month", l: "Ce mois" },
  { k: "all", l: "Tout" },
];

export default function Payments() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  const [includeUpcoming, setIncludeUpcoming] = useState(false);
  const [summary, setSummary] = useState({ rows: [] });
  const [selected, setSelected] = useState(null);   // driver row
  const [detail, setDetail] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments/summary", { params: { period, include_upcoming: includeUpcoming } });
      setSummary(data);
    } catch { toast.error("Échec"); }
  }, [period, includeUpcoming]);

  const loadDetail = useCallback(async () => {
    if (!selected) { setDetail(null); return; }
    try {
      const { data } = await api.get(`/admin/drivers/${selected.driver_id}/payments`, { params: { period, include_upcoming: includeUpcoming } });
      setDetail(data);
    } catch { toast.error("Échec"); }
  }, [selected, period, includeUpcoming]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const togglePaid = async (ride) => {
    const next = ride.payment_status === "paid" ? "unpaid" : "paid";
    try {
      await api.put(`/rides/${ride.id}/payment-status`, { payment_status: next });
      toast.success(next === "paid" ? "Marqué payé" : "Marqué non payé");
      loadSummary(); loadDetail();
    } catch { toast.error("Échec"); }
  };

  return (
    <div data-testid="payments-page" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Button data-testid="payments-back" variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-lg font-medium text-white flex items-center gap-2">
            <CurrencyEur size={20} className="text-amber-500" /> Paiements chauffeurs
          </h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.k}
              data-testid={`period-${p.k}`}
              onClick={() => setPeriod(p.k)}
              className={`h-9 px-3 rounded-md border text-xs font-medium transition-colors ${period === p.k ? "bg-amber-500 text-black border-amber-500" : "bg-[#1A1A1A] text-zinc-300 border-zinc-700 hover:bg-zinc-800"}`}
            >
              {p.l}
            </button>
          ))}
          <label className="flex items-center gap-1.5 ml-auto text-xs text-zinc-400 cursor-pointer select-none">
            <input
              data-testid="include-upcoming"
              type="checkbox"
              checked={includeUpcoming}
              onChange={(e) => setIncludeUpcoming(e.target.checked)}
              className="w-4 h-4 rounded border-zinc-600 text-amber-500 focus:ring-amber-500 bg-[#0a0a0a]"
            />
            Inclure les courses à venir
          </label>
        </div>
        <p className="text-[11px] text-zinc-500 -mt-2">
          {includeUpcoming ? "Vue opérationnelle : inclut les courses attribuées mais non terminées." : "Par défaut : uniquement les courses terminées (les annulées et à venir sont exclues)."}
        </p>

        {summary.rows.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">Aucune course attribuée sur cette période.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {summary.rows.map(r => (
              <button
                key={r.driver_id}
                data-testid={`driver-payments-${r.driver_id}`}
                onClick={() => setSelected(r)}
                className={`text-left bg-[#121212] border rounded-lg p-4 transition-colors ${selected?.driver_id === r.driver_id ? "border-amber-500/50" : "border-zinc-800 hover:border-zinc-700"}`}
              >
                <p className="text-white font-medium">{r.driver_name}</p>
                <p className="text-xs text-zinc-500 mt-1">{r.rides_count} courses</p>
                <div className="flex items-center gap-3 mt-2 text-sm">
                  <span className="text-amber-500 font-semibold">{r.total.toFixed(2)} EUR</span>
                  <span className="text-emerald-400">Payé {r.paid_total.toFixed(2)}</span>
                  <span className="text-red-400">Dû {r.unpaid_total.toFixed(2)}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {detail && (
          <div data-testid="driver-detail-panel" className="bg-[#121212] border border-zinc-800 rounded-lg p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-white font-medium">{selected.driver_name} — détail</p>
              <button className="text-xs text-zinc-500 hover:text-white" onClick={() => setSelected(null)}>Fermer</button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center mb-4">
              <Stat label="Total" value={`${detail.total.toFixed(2)} EUR`} color="text-amber-500" />
              <Stat label="Payé" value={`${detail.paid_total.toFixed(2)} EUR`} color="text-emerald-400" />
              <Stat label="Dû" value={`${detail.unpaid_total.toFixed(2)} EUR`} color="text-red-400" />
            </div>
            <div className="space-y-1.5">
              {detail.rides.map(r => (
                <div key={r.id} data-testid={`driver-ride-${r.id}`} className="flex items-center gap-3 bg-[#1A1A1A] rounded p-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{r.route_label}</p>
                    <p className="text-xs text-zinc-500">{fmtDate(r.pickup_date)} {r.pickup_time} · {r.driver_fare} EUR</p>
                  </div>
                  <StatusBadge status={r.status} />
                  <Button
                    data-testid={`toggle-paid-${r.id}`}
                    variant="ghost" size="sm"
                    onClick={() => togglePaid(r)}
                    className={`h-8 px-2 text-xs ${r.payment_status === "paid" ? "text-emerald-400" : "text-red-300"} border border-zinc-800`}
                  >
                    {r.payment_status === "paid" ? <CheckCircle size={14} className="mr-1" weight="fill" /> : <XCircle size={14} className="mr-1" />}
                    {r.payment_status === "paid" ? "Payé" : "Non payé"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-[#0e0e0e] border border-zinc-800 rounded-md p-3">
      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className={`text-base font-semibold ${color}`}>{value}</p>
    </div>
  );
}
